import React, { useState, useRef, useEffect, ChangeEvent } from "react";
import styled from "styled-components";
import Label from "../../label";
import { useTranslation } from "react-i18next";
import { Color } from "../../../constants/color";
import { BreakpointsQuery } from "../../../constants/device";
import { Icons } from "../../../constants/icons";
import { UserPageResponse } from "../../../types";
import {
  Disclosure,
  DisclosureContent,
  DisclosureProvider,
} from "@ariakit/react";
import { ChevronDown, Download, Trash2, X } from "lucide-react";
import Button from "../../button/button";
import Suggerimento from "../../tooltip/suggerimento";
import { axiosInstance } from "../../../provider/authProvider";
import { BE_BASE_URL } from "../../../utils/config";
import { nomeFileScaricato } from "../../../utils/nomeFileScaricato";
import { toast, Bounce } from "react-toastify";
import DialogConferma from "../../modal/conferma";
import { AxiosError, AxiosResponse } from "axios";
import { useAppSelector } from "../../../app/hooks";
import { selectUser } from "../../../features/user/userSlice";
import { puoGestireManuale, puoScaricareManuale } from "../../../constants/role";

interface Props {
  companyData: UserPageResponse | undefined;
}

const PlatformManualContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 1240px;
  max-width: 96%;
  margin-bottom: 2rem;
`;

const ManualUploadContainer = styled.div`
  background-color: white;
  border-radius: 16px;
  width: 45%;
  padding: 1rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  margin: 0 auto;
`;

interface NameContainerProps extends React.HTMLAttributes<HTMLSpanElement> {
  alt?: string;
}

const NameWrapper: React.FC<NameContainerProps> = ({
  alt,
  children,
  ...props
}) => (
  <NameContainer title={alt} {...props}>
    {children}
  </NameContainer>
);

const NameContainer = styled.span<NameContainerProps>`
  width: 33%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${Color.LIGHT_BLUE};
  font-weight: bold;
  font-family: inherit;
  font-size: 0.85rem;
`;

const DownloadDeleteContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: end;
  align-items: center;
  gap: 1rem;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column; /* Cambiato per accogliere il nuovo contenitore in verticale */
  justify-content: space-around;
  align-items: center;
  border-radius: 20px;
  box-shadow: 0 0 20px 10px ${Color.SHADOW};
  width: 1240px;
  max-width: 96%;
  padding: 1.3rem; /* Aggiunto padding per evitare che il contenitore tocchi i bordi */
  @media ${BreakpointsQuery.tablet} {
    width: 960px;
  }
`;

const TopSection = styled.div`
  display: flex;
  justify-content: space-between;
  column-gap: 100px;
  width: 100%;
  @media ${BreakpointsQuery.tablet} {
    column-gap: unset;
  }
`;

const DescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const SummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
`;

const StyledDisclosure = styled(Disclosure)`
  border-radius: 0.8rem;
  background-color: ${Color.WHITE};
  width: 100%;
  position: relative;
  overflow: hidden;
  border: none;

  padding: 0rem 1.5rem;
  margin-top: 1rem;

  & p[role='button'] {
    position: absolute;
    right: 0;
    top: 2px;
    bottom: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 2rem;
    border-radius: 0.8rem 0 0 0.8rem;
    background-color: ${Color.BLUE};
    color: ${Color.WHITE};
    font-weight: bold;
`;

const StyledDisclosureContent = styled(DisclosureContent)`
  background-color: ${Color.CERAMIC};
  padding: 1rem 2rem;
  border-radius: 10px;
  text-align: left;
  color: ${Color.DARK_BLUE};
  flex-wrap: wrap;
`;

const CourseList = styled.ul`
  columns: 2;
  list-style-type: disc;
  padding-left: 1.5rem;
  margin: 0;
`;

const CourseItem = styled.li`
  margin-bottom: 0.5rem; /* Add some space between items */
`;

const ProfileHeader = ({ companyData }: Props) => {
  const { t } = useTranslation();
  const [manualFile, setManualFile] = useState<File | null>(null);
  const manualFileInputRef = useRef<HTMLInputElement | null>(null);
  const [isManualUploaded, setIsManualUploaded] = useState(false);
  /*
   L'eliminazione del manuale non si torna indietro: si chiede conferma nella dialog
   dell'applicazione, non con window.confirm.
   */
  const [confermaEliminazione, setConfermaEliminazione] = useState(false);
  const [manualFileName, setManualFileName] = useState<string>("");
  /*
   Caricare ed eliminare il manuale lo può solo il SUPERADMIN, scaricarlo anche l'ADMIN (vedi
   `puoGestireManuale` e `puoScaricareManuale`): agli altri il backend risponde 403, e il nome
   del file resta l'unica cosa che vedono.
   */
  const utente = useAppSelector(selectUser);
  const gestisceManuale = puoGestireManuale(utente?.role);
  const scaricaManuale = puoScaricareManuale(utente?.role);

  useEffect(() => {
    fetchManualName();
  }, []);

  const handleManualFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    if (target.files) {
      const uploadedFile = target.files[0];
      setManualFile(uploadedFile);

      // Carica automaticamente il file quando selezionato
      if (uploadedFile) {
        const formData = new FormData();
        formData.append("file", uploadedFile);
        formData.append("docType", "manual");

        axiosInstance
          .patch(`${BE_BASE_URL}/company`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(() => {
            toast.success("Manuale caricato con successo", {
              position: "top-right",
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              theme: "colored",
              transition: Bounce,
            });
            fetchManualName();
            setManualFile(null);
            if (manualFileInputRef.current) {
              manualFileInputRef.current.value = "";
            }
          })
          .catch((error) => {
            const axiosError = error as AxiosError;
            let errorMessage = "Errore durante il salvataggio del manuale.";
            if (axiosError.response) {
              const data = axiosError.response.data as any;
              if (data && data.message) {
                errorMessage = data.message;
              }
            } else if (axiosError.message) {
              errorMessage = axiosError.message;
            }
            toast.error(errorMessage, {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              theme: "colored",
              transition: Bounce,
            });
          });
      }
    }
  };

  const handleManualClickRef = () => {
    if (manualFileInputRef.current) {
      manualFileInputRef.current.click();
    }
  };

  const fetchManualName = async () => {
    try {
      const axiosResponse = await axiosInstance.get(
        `${BE_BASE_URL}/company/manual-name`
      );

      if (axiosResponse.status === 200) {
        // Gestione di entrambi i casi: risposta come stringa o oggetto con fileName
        let fileName = "";
        if (typeof axiosResponse.data === "string") {
          fileName = axiosResponse.data;
          //console.log("Nome file ricevuto come stringa:", fileName);
        } else if (axiosResponse.data && axiosResponse.data.fileName) {
          fileName = axiosResponse.data.fileName;
          //console.log("Nome file ricevuto come oggetto:", fileName);
        }

        // Imposta il nome del file e il flag di upload solo se il nome è valido
        if (fileName && fileName.trim() !== "") {
          setManualFileName(fileName);
          setIsManualUploaded(true);
          //console.log("Nome file impostato:", fileName);
        } else {
          //console.log("Nome file non valido o vuoto");
        }
      }
    } catch (e) {
      const error = e as AxiosError;
      toast.error("Errore durante il recupero del manuale", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        transition: Bounce,
      });
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axiosInstance({
        url: `${BE_BASE_URL}/company/manual`,
        method: "GET",
        responseType: "blob",
      });

      // Oggi `GET /company/manual` non manda Content-Disposition e vale il ripiego; se un
      // giorno la manda, il nome esatto sta in `filename*`: vedi nomeFileScaricato.
      const filename = nomeFileScaricato(
        response.headers["content-disposition"],
        "manuale_piattaforma.pdf"
      );

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Download del manuale completato", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        transition: Bounce,
      });
    } catch (error) {
      const axiosError = error as AxiosError;
      let errorMessage = "Errore durante il download del manuale.";
      if (axiosError.response) {
        const data = axiosError.response.data as any;
        if (data && data.message) {
          errorMessage = data.message;
        }
      } else if (axiosError.message) {
        errorMessage = axiosError.message;
      }
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        transition: Bounce,
      });
    }
  };

  const eliminaManuale = async () => {
    try {
      const response = await axiosInstance.delete(
        `${BE_BASE_URL}/company/manual`
      );

      if (response.status === 204) {
        toast.success("Manuale eliminato con successo", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
          transition: Bounce,
        });
        // Reset immediato dello stato per aggiornare l'UI
        setIsManualUploaded(false);
        setManualFileName("");
        fetchManualName();
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      let errorMessage = "Errore durante l'eliminazione del manuale.";
      if (axiosError.response) {
        const data = axiosError.response.data as any;
        if (data && data.message) {
          errorMessage = data.message;
        }
      } else if (axiosError.message) {
        errorMessage = axiosError.message;
      }
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        transition: Bounce,
      });
    }
  };

  return (
    <>
      <PlatformManualContainer>
        <Label
          text="Manuale piattaforma"
          fontSize={"26px"}
          fontWeight={"bold"}
          textTransform={"uppercase"}
        />
        <ManualUploadContainer>
          <Label text="Manuale piattaforma:" style={{ width: "25%" }}></Label>{" "}
          <NameWrapper alt={manualFileName} title={manualFileName}>
            {manualFileName ? manualFileName : "Nessun file caricato"}
          </NameWrapper>
          {gestisceManuale && (<>
          <Suggerimento
            testo={t("user-aiuto-carica-manuale")}
            style={{ width: "25%" }}
          >
            <Button
              label={t("user-main-info-upload-button")}
              color={Color.BLUE}
              icon={Icons.BUTTONS.UPLOAD}
              style={{ width: "100%" }}
              onClick={handleManualClickRef}
            />
          </Suggerimento>
          <input
            id="manual-upload"
            type="file"
            ref={manualFileInputRef}
            style={{ display: "none" }}
            onChange={handleManualFileUpload}
          />
          </>)}
          <DownloadDeleteContainer>
            {isManualUploaded && scaricaManuale && (
              <button
                aria-label="Scarica il manuale della piattaforma"
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: "blue",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  justifyContent: "center",
                  flexShrink: "0",
                }}
                onClick={handleDownload}
              >
                <Download width={22} height={22} />
              </button>
            )}
            {isManualUploaded && gestisceManuale && (
              <button
                aria-label="Elimina il manuale della piattaforma"
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: "red",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  justifyContent: "center",
                  flexShrink: "0",
                }}
                onClick={() => setConfermaEliminazione(true)}
              >
                <Trash2 width={22} height={22} />
              </button>
            )}
          </DownloadDeleteContainer>
        </ManualUploadContainer>
      </PlatformManualContainer>
      <HeaderContainer>
        <TopSection>
          <DescriptionContainer>
            <Label
              text={t("user-summary-label")}
              fontSize={"30px"}
              fontWeight={"bold"}
            />
            <Label
              text={t("user-summary-description")}
              fontSize={"14px"}
              width={"340px"}
            />
          </DescriptionContainer>
          {/*
          <img src={Icons.COMMONS.USER} alt={"user avatar"} width={"100px"} />
          */}
          <SummaryContainer>
            <Label
              text={
                companyData
                  ? companyData.adminFirstName + " " + companyData.adminLastName
                  : ""
              }
              fontSize={"30px"}
              fontWeight={"bold"}
              textAlign={"right"}
              textTransform={"capitalize"}
            />
            <Label
              text={companyData ? companyData.adminEmail : ""}
              fontSize={"14px"}
              fontWeight={"bold"}
              width={"340px"}
              textAlign={"right"}
              textTransform={"lowercase"}
            />
            <Label
              text={companyData ? companyData.businessName : ""}
              fontSize={"14px"}
              fontWeight={"bold"}
              width={"340px"}
              textAlign={"right"}
              textTransform={"capitalize"}
            />
          </SummaryContainer>
        </TopSection>
        <DisclosureProvider>
          <StyledDisclosure>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "30px",
                fontWeight: "bold",
                color: Color.LIGHT_BLUE,
                fontFamily: "Avenir Next Bold, sans-serif",
                textAlign: "center",
              }}
            >
              <p style={{ margin: "0" }}>
                Abbonamento: {companyData ? companyData.subscriptionType : ""}
              </p>
              <p style={{ fontSize: "14px", margin: "0" }}>
                Cliccare per visualizzare i corsi acquistati
              </p>
            </div>
            <p aria-hidden="true">
              <ChevronDown color={Color.WHITE} size={32} />
            </p>
          </StyledDisclosure>
          <StyledDisclosureContent>
            {companyData &&
            companyData.subscriptionCourses &&
            Array.isArray(companyData.subscriptionCourses) ? (
              <CourseList>
                {companyData.subscriptionCourses
                  .slice() // crea una copia dell'array per non modificare l'originale
                  .sort((a, b) => a.localeCompare(b)) // ordina alfabeticamente
                  .map((course, index) => (
                    <CourseItem key={index}>{course}</CourseItem>
                  ))}
              </CourseList>
            ) : (
              <p>{t("no-courses")}</p>
            )}
          </StyledDisclosureContent>
        </DisclosureProvider>
      </HeaderContainer>
      <DialogConferma
        aperta={confermaEliminazione}
        titolo="Elimina manuale"
        messaggio="Sei sicuro di voler eliminare il manuale?"
        etichettaConferma="ELIMINA"
        coloreConferma={Color.RED}
        coloreAnnulla={Color.BLUE}
        onConferma={() => {
          setConfermaEliminazione(false);
          eliminaManuale();
        }}
        onAnnulla={() => setConfermaEliminazione(false)}
      />
    </>
  );
};

export default ProfileHeader;
